import { useState } from 'react';
import { IoClose } from "react-icons/io5";
import { sendTelegramMessage } from '../lib/telegram';
import { SITE_URL } from '../lib/jsonLd';
import { formatPhone } from '../lib/urls';
import { useTr } from '../lib/i18n';
import { Link } from 'react-router-dom';

// "Ești interesat? Trimite cerere" popup for listing detail pages:
// title + first image (horizontal), name/phone inputs,
// direct contact via phone call or WhatsApp.
const InterestModal = ({ listing, base, onClose }) => {
    const tr = useTr();
    const lang = tr('ro', 'ru');
    const l = listing?.listing || {};
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const locSlug = l.location?.slug ?? l.location?.id;
    const listingUrl = l.slug
        ? `${SITE_URL}/${base}/${locSlug}/${l.slug}`
        : `${SITE_URL}/${base}/${listing?.id}`;
    const firstImage = l.images?.[0] || l.thumbnails?.[0];
    const agentPhone = formatPhone(l.user?.phone);

    const waNumber = String(agentPhone || '').replace(/[^\d]/g, '');
    const waLink = waNumber
        ? `https://wa.me/${waNumber}?text=${encodeURIComponent(
            lang === 'ru'
                ? `Здравствуйте, меня интересует ${listingUrl}`
                : `Bună ziua, sunt interesat de ${listingUrl}`
        )}`
        : null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await sendTelegramMessage(
                (lang === 'ru' ? `Заявка из попапа (${base})\n` : `Cerere din popup (${base})\n`) +
                `${tr('Anunț', 'Объявление')}: ${lang === 'ru' ? (l.ru_seo_title || l.seo_title || l.street) : (l.seo_title || l.street)}\n` +
                `${tr('Link', 'Ссылка')}: ${listingUrl}\n` +
                `${tr('Nume', 'Имя')}: ${name}\n` +
                `${tr('Telefon', 'Телефон')}: ${phone}`
            );
            setSent(true);
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-[12px]"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-[5px] shadow-xl w-full max-w-[420px] p-[20px] flex flex-col gap-[16px]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-start gap-[10px]">
                    <p className='font-poppins text-[17px] font-[500]'>{tr('Ești interesat?', 'Заинтересованы?')}</p>
                    <button
                        onClick={onClose}
                        className="cursor-pointer text-gray-500 hover:text-[#d5ab63] transition-all duration-100 ease-in"
                        aria-label={tr('Închide', 'Закрыть')}
                    >
                        <IoClose className="text-2xl" />
                    </button>
                </div>

                {/* Anunțul: titlu orizontal + prima imagine în dreapta */}
                <div className="flex items-center justify-between gap-[12px] border border-gray-200 rounded-[5px] p-[10px]">
                    <p className='text-[14px] font-body font-[500] leading-[1.35] line-clamp-3 flex-1'>
                        {l.seo_title || l.street}
                    </p>
                    {firstImage && (
                        <img
                            src={firstImage}
                            alt={lang === 'ru' ? (l.ru_seo_title || l.seo_title) : l.seo_title}
                            className="w-[76px] h-[76px] object-cover rounded-[5px] shrink-0" loading="lazy" decoding="async"
                        />
                    )}
                </div>

                {sent ? (
                    <div className="flex flex-col gap-[10px] text-center py-[10px]">
                        <p className='text-[16px] font-poppins font-[500] text-green-700'>{tr('Cererea a fost trimisă!', 'Заявка отправлена!')}</p>
                        <p className='text-[14px] font-body text-gray-600'>{tr('Te contactăm în cel mai scurt timp.', 'Мы свяжемся с вами в ближайшее время.')}</p>
                        <button onClick={onClose} className='font-body text-[14px] text-[#b48a4d] hover:underline cursor-pointer'>{tr('Închide', 'Закрыть')}</button>
                    </div>
                ) : (
                    <>
                        <form onSubmit={handleSubmit} className="flex flex-col gap-[10px]">
                            <input
                                type="text"
                                required
                                placeholder={tr('Nume', 'Имя')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="font-body px-[14px] py-[10px] rounded-[5px] outline-none border-[1px] border-gray-300 text-[15px]"
                            />
                            <input
                                type="tel"
                                required
                                placeholder={tr('Număr de telefon', 'Номер телефона')}
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                className="font-body px-[14px] py-[10px] rounded-[5px] outline-none border-[1px] border-gray-300 text-[15px]"
                            />
                            <p className="text-[12px] font-body text-gray-500 leading-[1.55]">
                                {lang === 'ru'
                                    ? <>Заполняя форму и нажимая кнопку отправки, вы подтверждаете, что ознакомились с <Link to="/ru/usloviya-ispolzovaniya" className="underline hover:text-[#b48a4d]">Условиями использования</Link> и <Link to="/ru/politika-konfidentsialnosti" className="underline hover:text-[#b48a4d]">Политикой конфиденциальности</Link>, и соглашаетесь на получение ответа по отправленному запросу.</>
                                    : <>Prin completarea formularului și apăsarea butonului de trimitere, confirmați că ați citit <Link to="/termeni-si-conditii" className="underline hover:text-[#b48a4d]">Termenii și condițiile</Link> și <Link to="/politica-de-confidentialitate" className="underline hover:text-[#b48a4d]">Politica de confidențialitate</Link> și sunteți de acord să fiți contactat în legătură cu solicitarea transmisă.</>
                                }
                            </p>
                            <button
                                type="submit"
                                disabled={sending}
                                className="btn-main text-white text-[14px] font-[500] font-body py-[10px] rounded-[5px] cursor-pointer disabled:opacity-60"
                            >
                                {sending ? tr('Se trimite...', 'Отправка...') : tr('Trimite cererea', 'Отправить заявку')}
                            </button>
                        </form>

                        <div className="flex flex-col gap-[6px] pt-[2px]">
                            <p className='text-[13px] font-body text-gray-500'>{tr('Sau contactează direct:', 'Или свяжитесь напрямую:')}</p>
                            <div className="flex justify-between gap-[10px]">
                                {agentPhone && (
                                    <a
                                        href={`tel:${agentPhone}`}
                                        className="flex flex-1 items-center justify-center gap-[6px] border-[1px] border-gray-300 rounded-[5px] px-[12px] py-[8px] text-[14px] font-body hover:border-[#d5ab63] hover:text-[#b48a4d] transition-all duration-100 ease-in"
                                    >
                                        {agentPhone}
                                    </a>
                                )}
                                {waLink && (
                                    <a
                                        href={waLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex flex-1 items-center justify-center gap-[6px] bg-[#25D366] text-white rounded-[5px] px-[12px] py-[8px] text-[14px] font-body hover:opacity-90 transition-all duration-100 ease-in"
                                    >
                                        WhatsApp
                                    </a>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default InterestModal;
